/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { t } from 'i18next';
import moment, { Moment } from 'moment';
import { Storage, Signer } from 'aws-amplify';
import { isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { Auth } from '@aws-amplify/auth';

import {
  ChatURL,
  DateFormat,
  DateRangeOptionIds,
  DateRangeOptionType,
  defaultValueInMiles,
  IPAddressAPI,
  JobStatuses,
  Meridiem,
  mileInMeters,
  PlatformType,
  PROCESS_EVENTS,
  REPORT_TYPE_TO_EVENT_KEY,
  S3BucketLevel,
  ScheduleLabels,
  StorageKeys,
  USER_PROCESSES,
  UserType,
  velocitiOrganizationId,
  WorkDayStatus,
  XMLHttpRequestResponseType,
} from '../Constants/App';
import { JOBS_ROUTE_TO_EVENT_KEY, MARKETPLACE_ROUTE_TO_EVENT_KEY } from '../../Modules/Vendor/Models/constants';

import { JobStatus as JobStatues, WorkOrderService } from '../../API';
import FacilityApiService from '../../Modules/Facility/Services/FacilityApiService';
import WorkOrdersApiService from '../../Modules/Jobs/Services/WorkOrdersService';
import { AddUserName, FieldNames } from '../../Modules/Jobs/Models/AddEditOneTimeJob.Model';
import { GetAllBids, GetAllJobs } from '../../Modules/Vendor/Models/Marketplace.model';
import {
  AddressComponent,
  CurrentAddress,
} from '../Components/Common/GoogleMapLocationSelector/GoogleMapLocationSelector.Model';
import { PatternType, PatternTypes, WEEK_DAYS } from '../../Modules/Vendor/Components/AllSchedules/AllSchedules.Model';
import { JobManagementType } from '../../Modules/Vendor/Components/AllJobs/AllJobs.Model';
import { IProcessEvents } from '../../routes/config';

import { FilterItem } from '../../Modules/Jobs/Components/JobsListView/JobsListView';
import awsConfig from '../aws-exports';
import theme from '../Themes/theme';
import { ILoginCustomer } from '../Models';

export const isMock = false;

export interface ITimeFormateResponse {
  hours: number;
  meridiem: string;
  mins?: number;
  sec: number;
  formattedMins: string | number;
}

/**
 * @param startDate
 * @param endDate
 * @returns It will return based on given dates are same or after
 */
export const isDateSameOrAfter = (startDate?: string, endDate?: string) => {
  if (startDate && endDate) {
    const formattedStartDate = moment(startDate).format(FieldNames.dateFormat);
    const formattedEndDate = moment(endDate).format(FieldNames.dateFormat);
    return moment(formattedEndDate).isSameOrAfter(formattedStartDate);
  }
  return false;
};

export const isDatesSameOrAfter = (startDate: Moment, endDate: Moment) => {
  const formattedStartDate = startDate ? startDate.format(FieldNames.dateFormat) : '';
  const formatteEndDate = endDate ? endDate.format(FieldNames.dateFormat) : '';
  if (startDate && endDate) {
    return moment(formatteEndDate).isSameOrAfter(formattedStartDate);
  }
  return false;
};

/**
 * @param startDate
 * @param endDate
 * @returns It will return based on given dates are same or before
 */
export const isDateSameOrBefore = (startDate?: string, endDate?: string) => {
  if (startDate && endDate) {
    const formattedStartDate = moment(startDate).format(FieldNames.dateFormat);
    const formattedEndDate = moment(endDate).format(FieldNames.dateFormat);
    return moment(formattedEndDate).isSameOrBefore(formattedStartDate);
  }
  return false;
};

/**
 * @param startDate
 * @param endDate
 * @returns It will return based on given dates are before
 */
export const isDateBefore = (startDate?: string, endDate?: string) => {
  if (startDate && endDate) {
    const formattedStartDate = moment(startDate).format(FieldNames.dateFormat);
    const formattedEndDate = moment(endDate).format(FieldNames.dateFormat);
    return moment(formattedStartDate).isBefore(formattedEndDate);
  }
  return false;
};

/**
 * @param startDate
 * @param endDate
 * @returns It will return based on given dates are after
 */
export const isDateAfter = (startDate?: string, endDate?: string) => {
  if (startDate && endDate) {
    const formattedStartDate = moment(startDate).format(FieldNames.dateFormat);
    const formattedEndDate = moment(endDate).format(FieldNames.dateFormat);
    return moment(formattedStartDate).isAfter(formattedEndDate);
  }
  return false;
};

/**
 * @returns It will return time in 24 hours format
 */
export const hours = Array.from({ length: 48 }, (_, hour) =>
  moment({
    hour: Math.floor(hour / 2),
    minutes: hour % 2 === 0 ? 0 : 30,
  }).format('HH:mm')
);

export const getStoredCustomerDetails = () => {
  try {
    const value = localStorage.getItem('customerDetails');

    if (value) {
      const parsedValue = JSON.parse(value);

      if (parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)) {
        const { organization, userId, username } = parsedValue;

        const organizationId = organization?.organizationId ?? '';
        const organizationName = organization?.organizationName ?? '';

        return {
          customerId: organizationId,
          organizationName,
          userId,
          username: username ?? '',
        };
      }
    }
  } catch (error) {
    console.error('Error parsing stored customer details:', error);
  }

  return { customerId: '', organizationName: '', username: '' };
};

export const getProcessData = () => {
  try {
    const value = localStorage.getItem('processData');
    if (value) {
      return JSON.parse(value);
    }
  } catch (error) {
    console.error('Error parsing stored process data:', error);
  }
};

export const getAuthDetails = () => {
  const value = localStorage.getItem('auth');
  const parsedValue = value && JSON.parse(value);
  if (parsedValue) {
    return { ...parsedValue?.attributes };
  }
  return {};
};

export const getAcronymName = (name: string) => {
  const words = name?.split(' ');
  if (!name) {
    return '';
  }
  if (words.length >= 2) {
    const acronym = words.map((word: string) => word.charAt(0)).join('');
    return acronym.toUpperCase();
  } else if (name.length < 5) {
    return name;
  } else {
    const abbreviation = name.substring(0, 4);
    return abbreviation.toUpperCase();
  }
};

export const getTimeFromGivenDate = (date: string, is24Hours = false): ITimeFormateResponse => {
  const now = moment(date);
  const hours = now.hour();
  const mins = now.minutes();
  const formattedMins = mins < 10 ? `0${mins}` : mins.toString();
  const sec = now.seconds();
  const meridiem = now.hour() >= 12 ? Meridiem.PM : Meridiem.AM;
  const hours24Formate = is24Hours ? hours : hours % 12 || 12;

  return {
    hours: hours24Formate,
    formattedMins,
    sec,
    meridiem,
  };
};

// `returnDurationInDays` returns the difference between start and stop date in number of days format
export const returnDurationInDays = (_startDate: Moment, _stopDate: Moment) => {
  return parseInt(_stopDate.diff(_startDate, 'days', true)?.toString(), 10);
};

export const getDurationDaysLeft = (endDate: Moment) => {
  const currentDate = moment(new Date().toJSON());
  if (1 / returnDurationInDays(currentDate, endDate) === -Infinity) {
    return 0;
  }
  if (returnDurationInDays(currentDate, endDate) >= 0) {
    return returnDurationInDays(currentDate, endDate) + 1;
  }
  return returnDurationInDays(currentDate, endDate);
};

// getDurationInDaysOrMonths returns the difference between start(Moment) and stop(Moment) date in number of days or months or both format including stop date
export const getDurationInDaysOrMonths = (startDate: string, stopDate: string, isExcludeCurrentDate?: boolean) => {
  const date1 = moment(startDate);
  const date2 = moment(stopDate);
  const months = moment(stopDate).diff(startDate, 'months');
  date1.add(months, 'months');
  const days = isExcludeCurrentDate ? getDaysBtnTwoDates(date1, date2) - 1 : getDaysBtnTwoDates(date1, date2);
  const dayText = t('jobAward:day');
  const daysText = t('jobAward:days');

  if (months === 0 && days === 0) return t('vendorBidCreation:todayLeft');

  if (days < 0 || months < 0) {
    return 'Negative';
  }
  if (days === 0) {
    return `${months} ${t('vendorBidCreation:months')}`;
  }
  if (months === 0) {
    return `${days} ${days === 1 ? dayText : daysText}`;
  }
  return `${months} ${months === 1 ? t('vendorBidCreation:month') : t('vendorBidCreation:months')} ${days} ${
    days === 1 ? dayText : daysText
  }`;
};

export const formatDaysLeft = (days: number) => {
  const isOverdue = days && days < 0;
  const totalDays = Math.abs(days);
  const dayText = t('jobAward:day');
  const daysText = t('jobAward:days');
  if (isOverdue) {
    return t('vendor:jobOverdue');
  } else {
    return `${totalDays ?? 0} ${totalDays === 1 || totalDays < 1 ? dayText : daysText}`;
  }
};

// returnDurationInDaysByMoment returns the difference between start(Moment) and stop(Moment) date in number of days format
export const returnDurationInDaysByMoment = (startDate: Moment, stopDate: Moment) => {
  return parseInt(stopDate.diff(startDate, 'days', true).toString(), 10);
};

// getDaysBtnTwoDates returns the difference between start(Moment) and stop(Moment) date in number of days format including stop date
export const getDaysBtnTwoDates = (startDate: Moment, endDate: Moment) => {
  if (1 / returnDurationInDaysByMoment(startDate, endDate) === -Infinity) {
    return 0;
  }
  if (returnDurationInDaysByMoment(startDate, endDate) >= 0) {
    return returnDurationInDaysByMoment(startDate, endDate) + 1;
  }
  return returnDurationInDaysByMoment(startDate, endDate);
};

export enum BidAwardType {
  Award = 'Awarded',
  Closed = 'Close for bids',
  Completed = 'Completed',
  Delayed = 'Delayed',
  InProgress = 'InProgress',
  Open = 'Open for bids',
  Priority = 'Priority',
}

export enum WorkOrderStatus {
  Created = 'Created',
  Accepted = 'Accepted',
  Started = 'Started',
  Expired = 'Expired',
  InProgress = 'In Progress',
  OnHold = 'On Hold',
  Completed = 'Completed',
  Rejected = 'Rejected',
  InComplete = 'In Complete',
}

export enum DraftStatus {
  Awarded = 'C',
  Bids = 'A',
  Drafts = 'D',
}

export enum JobDetails {
  Self = 'self',
  SubVendor = 'subVendor',
  EstimateWithinPeazy = 'estimateWithinPeazy',
  EstimateOutsidePeazy = 'estimateOutsidePeazy',
  All = 'All',
}

export enum JobExecution {
  Self = 'self',
  SubVendor = 'SubVendor',
  EstimateWithinPeazy = 'estimateWithinPeazy',
  EstimateOutsidePeazy = 'estimateOutsidePeazy',
  All = 'All',
}

export enum AssignmentCardType {
  ShiftTimings = 'shiftTimings',
  AssignTeam = 'assignTeam',
  AssignSupervisor = 'assignSupervisor',
  AssignAssociate = 'assignAssociate',
  SupervisorPunchIn = 'supervisorPunchIn',
  FacilityAccess = 'facilityAccess',
  ProofOfCompletionTasks = 'proofOfCompletionTasks',
  PreShiftChecklist = 'preShiftChecklist',
  ModifyQuote = 'modifyQuote',
}

export enum AccessOptions {
  AccessPin = 'Access pin',
  ContactPerson = 'Contact Person',
  KeyCard = 'Key card',
  LockboxWithKey = 'Lockbox with key',
}

export enum UserRoles {
  AccountHolder = 'AccountHolder',
  Admin = 'Admin',
  Associate = 'Associate/Technician',
  DistrictManager = 'DistrictManager',
  NonAdmin = 'NonAdmin',
  OTJAdmin = 'OTJAdmin',
  RegionalManager = 'RegionalManager',
  SubRegionalManager = 'Sub-RegionalManager',
  Supervisor = 'Supervisor',
}

export enum JobSize {
  Large = 'L',
  Medium = 'M',
  Small = 'S',
}

// `getJobStatus` returns the job status based on the type and draft status accordingly
export const getJobStatus = (
  _bidDueDate: Moment,
  _startDate: Moment,
  _endDate: Moment,
  dataStatus: string,
  draftStatus: string
) => {
  if ((dataStatus as JobStatues) === JobStatues.Awarded) return 'Awarded';
  if ((draftStatus as DraftStatus) === DraftStatus.Bids) {
    if (getDurationDaysLeft(_bidDueDate) >= 0) return 'Open for Bids';
    if (getDurationDaysLeft(_bidDueDate) < 0) return 'Closed for Bids';
  }
  if ((draftStatus as DraftStatus) === DraftStatus.Drafts) return 'Draft Saved';
  return '';
};

// `blobToData` converts the blob to image url
const blobToData = (blob: Blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

export const getFacilityIcon = async (logoUrl: string) => {
  let facilityIcon;
  try {
    const facilityIconBlob = await FacilityApiService.facilityIcon(logoUrl);
    if (facilityIconBlob?.data) {
      facilityIcon = await blobToData(facilityIconBlob.data);
    }

    return facilityIcon;
  } catch (e) {
    console.error('Error in fetching facility icon', e);
    return '';
  }
};

export const getAcronym = (name: string) => {
  if (name) {
    return name
      .split(' ')
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
  }
  return name;
};

/**
 * @param taskData
 * @returns group Objects based on given ID
 */
export const groupById = (taskData: any, groupBy: any) => {
  return taskData?.reduce(function (updatedResult: any, individualFacility: any) {
    // based on facilityid grouping the object
    updatedResult[individualFacility[groupBy]] = updatedResult[individualFacility[groupBy]] || [];
    updatedResult[individualFacility[groupBy]].push(individualFacility);
    return updatedResult;
  }, Object.create(null));
};

export const getMapDateRange = (filterType = 'Last 3 months') => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  if (filterType === 'Last week') {
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() - 6);
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() - today.getDay());
    return { startDate: startOfWeek, endDate: today };
  } else if (filterType === 'Yesterday') {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return { startDate: yesterday, endDate: yesterday };
  } else if (filterType === 'Today') {
    return { startDate: today, endDate: today };
  } else if (filterType === 'Tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return { startDate: tomorrow, endDate: tomorrow };
  } else if (filterType === 'Next week') {
    const startOfNextWeek = new Date(today);
    startOfNextWeek.setDate(today.getDate() + (7 - today.getDay()));
    const endOfNextWeek = new Date(startOfNextWeek);
    endOfNextWeek.setDate(startOfNextWeek.getDate() + 6);
    return { startDate: startOfNextWeek, endDate: endOfNextWeek };
  } else if (filterType === 'Last 3 months') {
    const startOfLast3Months = new Date(today);
    startOfLast3Months.setMonth(today.getMonth() - 3);
    // const endOfLast3Months = new Date(today);
    return { startDate: startOfLast3Months, endDate: today };
  } else {
    throw new Error('Invalid filter type');
  }
};

export const getStatus = (
  inprogress: number,
  approvedbysupervisor: number,
  approvedbycustomer: number,
  completed: number,
  totaltasks: number
  // noShowStatus: any
) => {
  if (inprogress > 0) {
    return 'In progress';
  } else if (approvedbysupervisor) {
    return 'Pending Reviews';
  } else if (completed === totaltasks) {
    return 'Complete';
  } else {
    return 'Incomplete';
  }
};

export const formatDateToYYMMDDFormat = (oldFormatDate: string | number | Date) => {
  return moment(oldFormatDate).format(FieldNames.dateFormat);
};

/**
 * @param {string} str - The string to be parsed.
 * @param {number} val - The number of decimal places to keep.
 * @returns {number} - The parsed float value.
 */
export function ParseFloat(str: string | string[], val: number) {
  str = str?.toString();
  str = str?.slice(0, str?.indexOf('.') + val + 1);
  return str ? Number(str) : 0;
}

export function isFloat(value: number) {
  return Number(value) === value && value % 1 !== 0;
}

export const fetchDataFromS3Bucket = async (
  navigate: (routeScreenName: string) => void,
  key: string,
  level = S3BucketLevel.public
) => {
  try {
    // await refreshSession(navigate);
    return await Storage.get(key, { level });
  } catch (e) {
    throw new Error('failed to fetch data');
  }
};

export function checkImageValid(url: string, onSuccess: () => void, onError: () => void): void {
  const img = new Image();

  img.onload = () => {
    onSuccess();
  };

  img.onerror = () => {
    onError();
  };

  img.src = url;
}

export const deepClone = (itemArr: any[]) => {
  return [...itemArr].map((dataItem) => {
    return {
      ...dataItem,
    };
  });
};

export function calculateTotalEstimationWithProfit(total: number, profitPercentage: number) {
  const profitAmount = (total * profitPercentage) / 100;
  return total + profitAmount;
}

export const requestBlob = (uri: string) => {
  return new Promise((resolve, reject) => {
    const xhr: any = new XMLHttpRequest();
    xhr.onload = () => resolve(xhr.response);
    xhr.onerror = () => reject(new TypeError('common:networkRequestFailed'));
    xhr.responseType = XMLHttpRequestResponseType.blob;
    xhr.open('GET', uri, true);
    xhr.send(null);
  });
};

export const uploadDocumentToS3Bucket = async (
  navigate: (routeScreenName: string) => void,
  s3BucketPath: any,
  url: any,
  config: any,
  fileExtension?: string
) => {
  let body;
  let storedDocument;
  let modifiedConfig = config;

  try {
    // await refreshSession(navigate);
    if (fileExtension && fileExtensionExtraction(fileExtension) !== '') {
      modifiedConfig = { ...config, contentType: fileExtensionExtraction(fileExtension) };
    }
    body = await requestBlob(url);
    storedDocument = await Storage.put(s3BucketPath, body, modifiedConfig);
    return { file: storedDocument, error: {} };
  } catch (error: any) {
    throw new Error(error);
  }
};

export const fileExtensionExtraction = (fileExtension: string) => {
  if (fileExtension === 'png') {
    return 'image/png';
  }
  if (fileExtension === 'jpg' || fileExtension === 'jpeg') {
    return 'image/jpeg';
  }
  if (fileExtension === 'heic') {
    return 'image/heic';
  }
  if (fileExtension === 'heif') {
    return 'image/heif';
  }
  if (fileExtension === 'pdf') {
    return 'application/pdf';
  }
  if (fileExtension === 'mp4') {
    return 'video/mp4';
  }
  if (fileExtension === 'mov') {
    return 'video/quicktime';
  }
  if (fileExtension === 'mp3') {
    return 'audio/mpeg';
  }
  return '';
};

const FILE_MAGIC_SIGNATURES: Record<string, { bytes: number[]; offset?: number }[]> = {
  'image/png': [{ bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] }],
  'image/jpeg': [{ bytes: [0xff, 0xd8, 0xff] }],
  // HEIC/HEIF are ISOBMFF containers: ftyp box at offset 4, brand at offset 8
  'image/heic': [
    { bytes: [0x66, 0x74, 0x79, 0x70, 0x68, 0x65, 0x69, 0x63], offset: 4 }, // ftyp + heic
    { bytes: [0x66, 0x74, 0x79, 0x70, 0x68, 0x65, 0x69, 0x78], offset: 4 }, // ftyp + heix
  ],
  'image/heif': [
    { bytes: [0x66, 0x74, 0x79, 0x70, 0x6d, 0x69, 0x66, 0x31], offset: 4 }, // ftyp + mif1
    { bytes: [0x66, 0x74, 0x79, 0x70, 0x6d, 0x73, 0x66, 0x31], offset: 4 }, // ftyp + msf1
  ],
  'video/mp4': [{ bytes: [0x66, 0x74, 0x79, 0x70], offset: 4 }],
  'video/quicktime': [
    { bytes: [0x66, 0x74, 0x79, 0x70], offset: 4 },
    { bytes: [0x6d, 0x6f, 0x6f, 0x76], offset: 4 },
  ],
  'audio/mpeg': [
    { bytes: [0x49, 0x44, 0x33] },
    { bytes: [0xff, 0xfb] },
    { bytes: [0xff, 0xf3] },
    { bytes: [0xff, 0xf2] },
  ],
  'application/pdf': [{ bytes: [0x25, 0x50, 0x44, 0x46] }],
};

export const validateFileMagicBytes = async (file: File): Promise<{ valid: boolean; error?: string }> => {
  const mimeType = file.type;
  const signatures = FILE_MAGIC_SIGNATURES[mimeType];

  if (!signatures) {
    return { valid: false, error: `File type "${mimeType}" is not allowed.` };
  }

  const buffer = await file.slice(0, 12).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  const matches = signatures.some(({ bytes: sig, offset = 0 }) => sig.every((byte, i) => bytes[offset + i] === byte));

  if (!matches) {
    return {
      valid: false,
      error: `"${file.name}" is invalid. File content does not match its extension.`,
    };
  }

  return { valid: true };
};

export const getTagValues = (tags: string | null) => {
  if (isEmpty(tags)) {
    return [];
  }
  const categoriesArray = tags?.split(';');
  const categoryNames: string[] = [];
  categoriesArray?.forEach((category: string) => {
    const parts = category.split('|');
    if (parts.length === 3) {
      categoryNames.push(parts[2]);
    }
  });
  return categoryNames;
};

export const removeJobLocalData = () => {
  const keysToRemove = [
    'jobName',
    'jobAttachments',
    'jobServices',
    'selectedCustomer',
    'selectedFacility',
    'startDate',
    'endDate',
    'startTime',
    'endTime',
    'bidDueDate',
  ];

  keysToRemove.forEach((key) => {
    localStorage.removeItem(key);
  });
};

export const getIpAddress = async () => {
  try {
    const response = await fetch(IPAddressAPI);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Fetch failed:', error);
    throw error;
  }
};

export const removeLeadingZeros = (value: string | number) => {
  const stringValue = String(value);
  const parsedValue = parseFloat(stringValue);
  if (isNaN(parsedValue)) {
    throw new Error('Input is not a valid number.');
  }
  // Remove leading zeros from the integer part
  const integerPart = parseInt(stringValue.split('.')[0], 10);
  return parseFloat(
    integerPart.toString() + (parsedValue % 1 !== 0 ? '.' + parsedValue.toString().split('.')[1] : '')
  ).toFixed(2);
};

export const joinByComma = (keywordsList: (string | null | undefined)[]) => {
  let joinedByCommas = '';
  if (keywordsList?.length) {
    keywordsList.forEach((keyword: string | null | undefined, index: number) => {
      if (keyword) {
        if (keywordsList.length - 1 !== index && keywordsList[index + 1]) {
          joinedByCommas += `${keyword}, `;
        } else {
          joinedByCommas += keyword;
        }
      }
    });
  } else {
    return joinedByCommas;
  }
  return joinedByCommas;
};

export function convertToReadableFormat(inputString: any) {
  const readableString = inputString?.replace(/([a-z])([A-Z])/g, '$1 $2');

  return readableString?.charAt(0)?.toUpperCase() + readableString?.slice(1);
}

export const getAuthData = () => {
  let role: UserType | '' = '';
  let userId: string = '';
  let isDistrictManager: boolean = false;
  const authValue = localStorage.getItem('auth');
  if (authValue !== null) {
    const authData = JSON.parse(authValue);
    role = authData?.attributes?.['custom:Role'] || '';
    userId = authData?.username || '';
    isDistrictManager = authData?.canCreateOneTimeJob ?? false;
  }
  return { role, userId, isDistrictManager };
};

export const getDefaultFormValue = (type: FieldNames) => {
  if (type === FieldNames.startDate) {
    return localStorage.getItem('startDate') ?? null;
  } else if (type === FieldNames.endDate) {
    return localStorage.getItem('endDate') ?? null;
  } else if (type === FieldNames.bidDueDate) {
    return localStorage.getItem('bidDueDate') ?? null;
  } else if (type === FieldNames.jobName) {
    return localStorage.getItem('jobName') ?? '';
  } else {
    return localStorage.getItem('searchedVendors') ?? '';
  }
};

export const getDefaultAddUserValue = (type: string) => {
  if ((type as AddUserName) === AddUserName.fullName) {
    return localStorage.getItem('name') ?? null;
  } else if ((type as AddUserName) === AddUserName.email) {
    return localStorage.getItem('email') ?? null;
  } else if ((type as AddUserName) === AddUserName.phoneNum) {
    return localStorage.getItem('phoneNumber') ?? '';
  } else {
    return localStorage.getItem('roleId') ?? '';
  }
};

export const getDefaultFacilitiesList = () => {
  const selectedFacility = localStorage.getItem('selectedFacility');
  return selectedFacility
    ? JSON.parse(selectedFacility)
    : [
        {
          facilityName: '',
          id: uuidv4(),
          facilityId: '',
          mdFacilityType: '',
          facilityError: '',
        },
      ];
};

export const getDefaultServiceValue = () => {
  const selectedService = localStorage.getItem('jobServices');
  return selectedService
    ? JSON.parse(selectedService)
    : [
        {
          service: '',
          id: uuidv4(),
          quantity: '',
          unit: '',
          mdServiceId: '',
          nameError: '',
          asError: '',
          name: '',
          tasks: '',
          tasksError: '',
        },
      ];
};

export const getDefaultServiceValueForScheduleCreation = () => {
  return [
    {
      service: '',
      id: uuidv4(),
      quantity: '',
      unit: '',
      mdServiceId: '',
      nameError: '',
      asError: '',
      name: '',
      tasks: '',
      tasksError: '',
    },
  ];
};
export const getDoubleDigitNumberString = (digit: number) => {
  if (Number(digit) < 10) {
    return `0${digit}`;
  }
  return digit;
};

export enum RolesEnum {
  AccountHolder = 'AccountHolder',
  AssociateTechnician = 'Associate/Technician',
}

export const removeNonValueFromStringArray = (inputArr: string[]) => {
  return inputArr.filter((item) => {
    return item ? true : false;
  });
};

export const getAccessFacilityLockKeyLabel = (label: AccessOptions) => {
  if (!label) return '';
  switch (label) {
    case AccessOptions.LockboxWithKey:
      return t('facility:lockBoxKey');
    case AccessOptions.KeyCard:
      return t('facility:keyCard');
    case AccessOptions.AccessPin:
      return t('facility:accessPin');
    default:
      return '';
  }
};

export enum AreaSize {
  L = '25000',
  S = '5000',
}

export const calculateWorkVolume = (services: Array<WorkOrderService>) => {
  let sum = 0;
  services.forEach((service: WorkOrderService) => {
    sum += service.serviceMeasure || 0;
  });
  if (sum >= Number(AreaSize.L)) return 'L';
  if (sum <= Number(AreaSize.S)) return 'S';
  return 'M';
};

export const getTimeFormat = (time: string) => {
  if (time) {
    const hour24 = time?.split(':');
    const minutes: string = time?.split(':')[1];
    const AmOrPm = +hour24[0] >= 12 ? 'PM' : 'AM';
    const hours = +hour24[0] % 12 || 12;
    return `${hours}:${minutes} ${AmOrPm}`;
  }
};

export const getTimeDifferenceInMinutes = (actualStartTime: string, actualEndTime: string) => {
  const startTime: Date = new Date(actualStartTime);
  const endTime: Date = new Date(actualEndTime);

  const timeDifferenceMs: number = endTime.getTime() - startTime.getTime();

  return Math.floor(timeDifferenceMs / (1000 * 60));
};

export const convertDateFormatToIso = (dateString: string): string => {
  try {
    const dateObject: Date = new Date(dateString);

    const isoDateString: string = dateObject.toISOString();

    return isoDateString;
  } catch (error) {
    console.error('Error converting date:', error);
    return '';
  }
};

export const refreshSession = async (navigate: (routeScreenName: string) => void) => {
  try {
    await Auth.currentSession();
    return true;
  } catch (e: any) {
    localStorage.removeItem('auth');
    localStorage.removeItem('customerDetails');
    await Auth.signOut();
    if (navigate) {
      navigate('/login');
    }
    return false;
  }
};

export const getDaySuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const getRoleName = (role?: string) => {
  if (!role) return '';
  switch (role as UserRoles) {
    case UserRoles.Admin:
      return t('roleName:admin');
    case UserRoles.NonAdmin:
      return t('roleName:nonAdmin');
    case UserRoles.AccountHolder:
      return t('roleName:accountHolder');
    case UserRoles.Associate:
      return t('roleName:associateOrTechnician');
    case UserRoles.DistrictManager:
      return t('roleName:districtManager');
    case UserRoles.RegionalManager:
      return t('roleName:regionalManager');
    case UserRoles.Supervisor:
      return t('roleName:supervisor');
    default:
      return '';
  }
};

export enum ErrorCodesForRescheduleShift {
  ALREADY_PUNCHED_IN = 'ALREADY_PUNCHED_IN',
  DATE_NOT_AVAILABLE = 'DATE_NOT_AVAILABLE',
  USER_NOT_ALLOWED_TO_RESCHEDULE = 'USER_NOT_ALLOWED_TO_RESCHEDULE',
  CUSTOMER_PROHIBITS_RESCHEDULE = 'CUSTOMER_PROHIBITS_RESCHEDULE',
  SHIFT_ALREADY_EXIST = 'SHIFT_ALREADY_EXIST',
}

export const getRescheduleShiftErrorMessage = (errorCode: ErrorCodesForRescheduleShift): string => {
  if (isEmpty(errorCode)) return '';
  switch (errorCode) {
    case ErrorCodesForRescheduleShift.ALREADY_PUNCHED_IN:
      return 'rescheduleShiftDate:rescheduleShiftHasPunchInRecords';
    case ErrorCodesForRescheduleShift.DATE_NOT_AVAILABLE:
      return 'rescheduleShiftDate:rescheduleShiftDateNotAvailable';
    case ErrorCodesForRescheduleShift.USER_NOT_ALLOWED_TO_RESCHEDULE:
      return 'rescheduleShiftDate:rescheduleShiftDateNotAuthorized';
    case ErrorCodesForRescheduleShift.CUSTOMER_PROHIBITS_RESCHEDULE:
      return 'rescheduleShiftDate:rescheduleShiftDateNotAllowedByCustomer';
    case ErrorCodesForRescheduleShift.SHIFT_ALREADY_EXIST:
      return 'rescheduleShiftDate:rescheduleShiftDateIsAlreadyExist';
    default:
      return '';
  }
};

export const getRescheduleShiftsErrorMessage = (errorCode: ErrorCodesForRescheduleShift): string => {
  if (isEmpty(errorCode)) return '';
  switch (errorCode) {
    case ErrorCodesForRescheduleShift.ALREADY_PUNCHED_IN:
      return 'rescheduleShiftDate:rescheduleShiftsHasPunchInRecords';
    case ErrorCodesForRescheduleShift.DATE_NOT_AVAILABLE:
      return 'rescheduleShiftDate:rescheduleShiftsDateNotAvailable';
    case ErrorCodesForRescheduleShift.USER_NOT_ALLOWED_TO_RESCHEDULE:
      return 'rescheduleShiftDate:rescheduleShiftsDateNotAuthorized';
    case ErrorCodesForRescheduleShift.CUSTOMER_PROHIBITS_RESCHEDULE:
      return 'rescheduleShiftDate:rescheduleShiftsDateNotAllowedByCustomer';
    case ErrorCodesForRescheduleShift.SHIFT_ALREADY_EXIST:
      return 'rescheduleShiftDate:rescheduleShiftsDateIsAlreadyExist';
    default:
      return '';
  }
};

export const formatDate = (
  date?: string,
  format:
    | 'MM-DD-YYYY'
    | 'MM/DD/yyyy'
    | 'MM/DD/YYYY'
    | 'YYYY-MM-DD'
    | 'MMM D, YYYY'
    | 'MMM D, YYYY HH:mm:ss.sss' = 'MM-DD-YYYY'
): string => {
  if (!date) return '-';
  const formattedDate = date.endsWith('Z') ? date.slice(0, -1) : date;
  return moment(formattedDate).format(format);
};

export const getFormatDate = (date?: string): string => {
  if (!date) return '-';
  const formattedDate = date.endsWith('Z') ? date.slice(0, -1) : date;
  return moment(formattedDate).format(DateFormat.ISODate);
};

export const formattingDate = (date: string | undefined): string => {
  if (!date) return '';
  return date.endsWith('Z') ? date.slice(0, date.length - 1) : date;
};

export const getDateAfterAddingZForTimeZone = (date: string) => {
  if (date) {
    return date?.endsWith('Z') ? date : `${date}Z`;
  }
  return '';
};

export const parseTime = (time: any) => {
  const [hours, minutes] = time.split(':').map(Number);
  return { hours, minutes };
};

export enum ErrorCodesForPunChIn {
  ALREADY_PUNCHED_IN = 'ALREADY_PUNCHED_IN',
  ALREADY_EXIST = 'ALREADY_EXIST',
}

export const getDateRange = (days: number) => {
  const today = new Date();
  let fromDate = new Date(today);
  let toDate = new Date(today);
  if (days > 0) {
    fromDate = new Date(today);
    toDate.setDate(today.getDate() + days - 1); // To X days in the future
  } else if (days < 0) {
    fromDate.setDate(today.getDate() + days + 1); // From X days in the past
    toDate = new Date(today);
  }
  return { fromDate, toDate };
};

export const NUMBER_OF_ITEMS_PER_PAGE = 10;

export const getJobBids = (selectedBid: GetAllJobs, customerId: string) => [
  {
    bidId: selectedBid?.bidId ?? '',
    jobId: selectedBid?.jobId ?? '',
    bidName: selectedBid?.jobName ?? '',
    vendorId: customerId,
  },
];

export const getVendorBids = (selectedBid: GetAllBids, customerId: string) => [
  {
    bidId: selectedBid?.bidId ?? '',
    jobId: selectedBid?.jobId ?? '',
    bidName: selectedBid?.jobName ?? '',
    finalQuote: selectedBid?.finalQuote,
    bidCreationDate: selectedBid?.bidCreatedOn,
    draftStatus: selectedBid?.bidDraftStatus,
    dataStatus: selectedBid?.bidDataStatus,
    bidVersion: selectedBid?.bidVersion,
    vendorId: customerId,
  },
];

export const getJobFacility = (selectedBid: GetAllJobs) => ({
  jobId: selectedBid?.jobId ?? '',
  facilityAddress: selectedBid?.jobFacilities?.[0]?.facilityAddress ?? '',
  status: null,
  jobStartDate: selectedBid?.jobStartDate,
  jobEndData: selectedBid?.jobStopDate,
});

export const getBidFacility = (selectedBid: GetAllBids) => ({
  jobId: selectedBid?.jobId ?? '',
  facilityAddress: selectedBid?.jobFacilities?.[0]?.facilityAddress ?? '',
  status: null,
  jobStartDate: selectedBid?.startDate,
  jobEndData: selectedBid?.stopDate,
});

export const getBidAddress = (selectedBid: GetAllBids) => ({
  addressName: selectedBid?.jobFacilities?.[0]?.facilityAddress ?? '',
  noOfBuildings: 1,
  addressLine1: selectedBid?.jobFacilities?.[0]?.facilityAddress ?? '',
  addressLine2: selectedBid?.jobFacilities?.[0]?.facilityAddress ?? '',
  addressLine3: selectedBid?.jobFacilities?.[0]?.facilityAddress ?? '',
});

export const getJobAddress = (selectedBid: GetAllJobs) => ({
  addressName: selectedBid?.jobFacilities?.[0]?.facilityAddress ?? '',
  addressLine1: selectedBid?.jobFacilities?.[0]?.facilityAddress ?? '',
  addressLine2: selectedBid?.jobFacilities?.[0]?.facilityAddress ?? '',
  addressLine3: selectedBid?.jobFacilities?.[0]?.facilityAddress ?? '',
});

export const handleSort = async (
  sortingObj: any,
  currentSortOrder: string,
  setSorting: (object: any[]) => void,
  setNewSortOrder: (objectId: string) => void,
  setNewSort: (objectType: boolean) => void,
  fetchFunction: (
    pageNumber?: number,
    limit?: number,
    sortBy?: any,
    sortOrder?: boolean,
    filter?: string
  ) => Promise<void>,
  hasFetchedDataRef: React.MutableRefObject<boolean>,
  currentSort?: boolean,
  currentPage?: number,
  itemsPerPage?: number
) => {
  if (sortingObj?.length && (sortingObj[0].id !== currentSortOrder || sortingObj[0].desc !== currentSort)) {
    setSorting(sortingObj);
    setNewSortOrder(sortingObj[0].id);
    setNewSort(sortingObj[0].desc);

    await fetchFunction(currentPage, itemsPerPage, sortingObj[0].id, sortingObj[0].desc);
  } else {
    setSorting([]);
    if (hasFetchedDataRef.current) {
      await fetchFunction(currentPage, itemsPerPage);
    }
  }
};

export const handleFilter = async (
  filtersObj: FilterItem[],
  setColumnFilters: (object: any[]) => void,
  fetchFunction: (
    pageNumber?: number,
    limit?: number,
    sortBy?: any,
    sortOrder?: boolean,
    filterBy?: string,
    filter?: string
  ) => Promise<void>,
  currentPage: number,
  itemsPerPage: number,
  sortOrder: string,
  hasFetchedDataRef: React.MutableRefObject<boolean>,
  sortDirection?: boolean
) => {
  if (filtersObj?.length) {
    setColumnFilters(filtersObj);
    await fetchFunction(
      currentPage,
      itemsPerPage,
      sortOrder,
      sortDirection,
      filtersObj?.[0]?.id,
      filtersObj?.[0]?.value
    );
  } else {
    setColumnFilters([]);
    if (hasFetchedDataRef.current) {
      await fetchFunction(currentPage, itemsPerPage);
    }
  }
};
export const getWebInfo = () => {
  return {
    'x-pzy-client-version': '2.9.0',
    'x-pzy-client-type': PlatformType.Web,
    // Every GraphQL call spreads getWebInfo() into its headers, so this carries the loginToken on all of them.
    ...getLoginTokenHeader(),
  };
};
export const formatTime = (dateString: string | null) => {
  if (!dateString) {
    return '-';
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return '-';
  }
  const utcHours = date.getUTCHours();
  const utcMinutes = date.getUTCMinutes();
  const period = utcHours >= 12 ? 'PM' : 'AM';
  const hourIn12HrFormat = utcHours % 12 || 12;
  const formattedMinutes = utcMinutes < 10 ? `0${utcMinutes}` : utcMinutes;
  return `${hourIn12HrFormat}:${formattedMinutes} ${period}`;
};

export const formatTimeTo12Hr = (timeString: string | null) => {
  if (!timeString) {
    return '-';
  }
  const [hoursStr, minutesStr] = timeString.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  if (isNaN(hours) || isNaN(minutes)) {
    return '-';
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const hourIn12HrFormat = hours % 12 || 12;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${hourIn12HrFormat}:${formattedMinutes} ${period}`;
};

export const itemsPerPage = 10;

export function getChatUrls(environment: string) {
  let apiBaseUrl = ChatURL.ApiBaseUrlDev;
  let mqttClusterBaseUrl = ChatURL.MqttClusterBaseUrlDev;

  if (environment === 'dev') {
    apiBaseUrl = ChatURL.ApiBaseUrlDev;
    mqttClusterBaseUrl = ChatURL.MqttClusterBaseUrlDev;
  }
  if (environment === 'qa') {
    apiBaseUrl = ChatURL.ApiBaseUrlQa;
    mqttClusterBaseUrl = ChatURL.MqttClusterBaseUrlQa;
  }
  if (environment === 'staging') {
    apiBaseUrl = ChatURL.ApiBaseUrlStaging;
    mqttClusterBaseUrl = ChatURL.MqttClusterBaseUrlStaging;
  }
  if (environment === 'pre-prod') {
    apiBaseUrl = ChatURL.ApiBaseUrlPreProd;
    mqttClusterBaseUrl = ChatURL.MqttClusterBaseUrlPreProd;
  }
  if (environment === 'prod') {
    apiBaseUrl = ChatURL.ApiBaseUrlProd;
    mqttClusterBaseUrl = ChatURL.MqttClusterBaseUrlProd;
  }
  return [apiBaseUrl, mqttClusterBaseUrl];
}

export const getPlaceholderText = (userName: string, companyName?: string): string => {
  if (!isEmpty(userName) && !isEmpty(companyName)) return `To : ${userName?.trim()} @ ${companyName?.trim()}`;
  if (!isEmpty(companyName)) return `To : ${companyName?.trim()}`;
  if (!isEmpty(userName)) return `To : ${userName?.trim()}`;
  return '';
};

export const convertToObject = (value: string | object) => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error('Invalid JSON string:', value);
      return null;
    }
  }
  return value;
};

export const removeMemberFromAlreadyCreatedGroup = async (
  removedAssociates: string[],
  createdGroup: any,
  RemoveUserFromConversation: (params: { userIds: string[]; conversationId: string }) => Promise<any>
) => {
  try {
    const userIds: string[] = removedAssociates;
    await RemoveUserFromConversation?.({
      userIds,
      conversationId: createdGroup?.[0]?.id,
    });
  } catch (e) {
    console.log('error', e);
  }
};

export const deleteExistingConversation = async (
  conversationId: string,
  deleteConversation: (conversationId: string) => Promise<any>
) => {
  try {
    await deleteConversation?.(conversationId);
  } catch (e) {
    console.log('error', e);
  }
};

const getCreatedGroup = (data: any, jobId: string, isDeletingAssociate: boolean) => {
  return data?.filter((conversation: any) => {
    let metaObject;
    if (typeof conversation?.meta === 'string') {
      metaObject = JSON.parse(conversation?.meta);
    } else {
      metaObject = conversation?.meta;
    }
    if (metaObject?.currentJobId === jobId && metaObject?.isFromJob === true && conversation?.mode === 'GROUP') {
      return isDeletingAssociate ? metaObject?.isAssociateGroup : metaObject?.isSupervisorGroup;
    }
  });
};

export const removeUsersFromConversation = async (
  data: any,
  removedAssociates: string[],
  jobId: string,
  isDeletingAssociate: boolean,
  workOrderId: string,
  selectedScheduleId: string,
  RemoveUserFromConversation: (params: { userIds: string[]; conversationId: string }) => Promise<any>,
  deleteConversation: (conversationId: string) => Promise<any>,
  loggedInUserId?: string
) => {
  try {
    const createdGroup = getCreatedGroup(data, jobId, isDeletingAssociate);

    const teamAssignedToWorkOrder = await WorkOrdersApiService.getWorkOrderTeams({
      workOrderId,
      woScheduleMasterId: selectedScheduleId,
    });

    const teamData = teamAssignedToWorkOrder?.data?.filter((user: { isSupervisor: any }) =>
      isDeletingAssociate ? !user?.isSupervisor : user?.isSupervisor
    );

    if (!isEmpty(createdGroup) && !isEmpty(removedAssociates)) {
      const associatesToRemove = removedAssociates?.filter((user) => createdGroup?.[0]?.userIds?.includes(user));

      if (!isEmpty(associatesToRemove)) {
        await removeMemberFromAlreadyCreatedGroup(associatesToRemove, createdGroup, RemoveUserFromConversation);
      }

      const isAnyAssociateInExistingConversation =
        teamData?.filter((user: { userId: any }) => createdGroup?.[0]?.userIds?.includes(user?.userId)) ?? [];

      if (
        isEmpty(isAnyAssociateInExistingConversation) ||
        (isAnyAssociateInExistingConversation?.length === 1 &&
          isAnyAssociateInExistingConversation?.[0]?.userId === loggedInUserId)
      ) {
        deleteExistingConversation(createdGroup?.[0]?.id, deleteConversation);
      }
    }
  } catch (e) {
    console.log('error', e);
  }
};

export const formatTimeFromDateTime = (dateTime: string | null): string => {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '-';
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const AmOrPm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  return `${hours}:${formattedMinutes} ${AmOrPm}`;
};

export const formatUtcTime = (time: string, timeFormat = 'YYYY-MM-DD'): string => {
  if (!time) return '-';
  return moment(time).utc().format(timeFormat);
};

export const useDateRangeOptions = () => {
  return [
    { id: DateRangeOptionIds.Today, label: t('dashboard:today'), value: '1', type: DateRangeOptionType.Days },
    { id: DateRangeOptionIds.NextWeek, label: t('dashboard:nextWeek'), value: '7', type: DateRangeOptionType.Days },
    { id: DateRangeOptionIds.LastWeek, label: t('dashboard:lastWeek'), value: '-7', type: DateRangeOptionType.Days },
    {
      id: DateRangeOptionIds.CustomDateRange,
      label: t('dashboard:customDateRange'),
      value: 'custom',
      type: DateRangeOptionType.CustomDateRange,
    },
  ];
};

export const getReadableSchedule = (code: string): string => {
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const weekMap: { [key: string]: string } = {
    F: 'First',
    S: 'Second',
    T: 'Third',
    L: 'Last',
  };
  const [weekCode, dayIndex] = code.split('-');
  const weekText = weekMap[weekCode.trim().toUpperCase()];
  const dayText = daysOfWeek[parseInt(dayIndex, 10)];

  return weekText && dayText ? `${weekText} ${dayText}` : '-';
};

export const formatAsLocalDateString = (date: Date | null | undefined): string => {
  if (!date || isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
};

export const getMRTTableHeadStyles = (isFiltered: boolean, isLoading?: boolean) => ({
  sx: {
    border: `0.03125rem solid ${theme.palette.divider}`,
    verticalAlign: 'middle',
    '& .Mui-TableHeadCell-Content': {
      justifyContent: 'space-between',
      '& .MuiIconButton-root': {
        color: isFiltered ? theme.palette.primary.dark : undefined,
        opacity: isLoading ? 0.5 : 1,
        cursor: isLoading ? 'not-allowed' : 'pointer',
        pointerEvents: isLoading ? 'none' : 'auto',
      },
      '& .MuiCheckbox-indeterminate': {
        color: theme.palette.primary.main,
      },
    },
  },
});
export const getFormattedDates = (fromDate?: string, toDate?: string) => {
  const fromDateYMDFormat = fromDate ? formatDateToYYMMDDFormat(fromDate) : undefined;
  const toDateYMDFormat = toDate ? formatDateToYYMMDDFormat(toDate) : undefined;
  return { fromDateYMDFormat, toDateYMDFormat };
};

export const determineScheduleDate = (fromDateYMD?: string, toDateYMD?: string) => {
  if (!(fromDateYMD && toDateYMD) && (!isEmpty(fromDateYMD) || !isEmpty(toDateYMD))) {
    return fromDateYMD ?? toDateYMD;
  }
  if (fromDateYMD && toDateYMD && fromDateYMD === toDateYMD) {
    return fromDateYMD;
  }
  return '';
};

export async function signRequest<T = any>(
  request: { method: string; url: string; headers: Record<string, string> },
  service: string
): Promise<{ data: T; errors: any[] }> {
  const credentials = await Auth.currentCredentials();

  if (!credentials.accessKeyId || !credentials.secretAccessKey) {
    throw new Error('Invalid AWS credentials');
  }

  const loginTokenHeader = await getLoginTokenHeaderWhenReady();
  const signedRequest = Signer.sign(
    { ...request, headers: { ...request.headers, ...loginTokenHeader } },
    {
      access_key: credentials.accessKeyId,
      secret_key: credentials.secretAccessKey,
      session_token: credentials.sessionToken,
    },
    {
      service: service,
      region: awsConfig.aws_project_region,
    }
  );

  const response = await fetch(signedRequest.url, {
    method: signedRequest.method,
    headers: signedRequest.headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const responseJson = await response.json();
  return { data: responseJson as T, errors: [] };
}

export async function getAWSCredentials() {
  const credentials = await Auth.currentCredentials();

  if (!credentials.accessKeyId || !credentials.secretAccessKey) {
    throw new Error('Invalid AWS credentials');
  }

  return credentials;
}

// The session token the auth-events endpoint returns on SIGN_IN. Read from storage on every
// request so there is no second copy to go stale; cleared on sign-out.
export const setLoginToken = (loginToken: string): void => {
  try {
    localStorage.setItem(StorageKeys.LoginToken, loginToken);
  } catch (e) {
    console.error('Error saving login token', e);
  }
};

export const getLoginToken = (): string => {
  try {
    return localStorage.getItem(StorageKeys.LoginToken) ?? '';
  } catch {
    return '';
  }
};

export const getLoginTokenHeader = (): Record<string, string> => {
  const loginToken = getLoginToken();
  return loginToken ? { logintoken: loginToken } : {};
};

let loginSetup: Promise<unknown> | null = null;
let pendingSignIn: Promise<unknown> | null = null;

export const setLoginSetup = (setup: Promise<unknown> | null): void => {
  loginSetup = setup;
};

export const setPendingSignIn = (signIn: Promise<unknown> | null): void => {
  pendingSignIn = signIn;
};

const settle = async (step: Promise<unknown> | null): Promise<void> => {
  try {
    await step;
  } catch {
    // A failed login step just means there is no token to send.
  }
};

export const getLoginTokenHeaderWhenReady = async (waitForLoginSetup = true): Promise<Record<string, string>> => {
  if (waitForLoginSetup) await settle(loginSetup);
  await settle(pendingSignIn);
  return getLoginTokenHeader();
};

// The login form and the App.tsx sign-in handler run at the same time and ask for the same user lookups. Calls with
// the same key within a few seconds share one request instead of sending it twice. Cleared on sign-out.
const SHARED_CALL_TTL_MS = 30 * 1000;
const sharedCalls = new Map<string, { expiresAt: number; result: Promise<unknown> }>();

export const shareRecentCall = <T>(key: string, call: () => Promise<T>): Promise<T> => {
  const shared = sharedCalls.get(key);
  if (shared && shared.expiresAt > Date.now()) return shared.result as Promise<T>;
  const result = call();
  sharedCalls.set(key, { expiresAt: Date.now() + SHARED_CALL_TTL_MS, result });
  result.catch(() => sharedCalls.delete(key));
  return result;
};

export const clearSharedCalls = (): void => {
  sharedCalls.clear();
};

export const clearLoginToken = (): void => {
  try {
    localStorage.removeItem(StorageKeys.LoginToken);
  } catch {
    // ignore
  }
};

export const signedFetch = async ({
  method,
  url,
  body,
  requestId,
  userId,
  abnHeaders,
  waitForLoginToken = true,
}: {
  method: string;
  url: string;
  body?: any;
  requestId?: string;
  userId?: string;
  username?: string;
  // false only for the auth events themselves, which must not wait on the SIGN_IN they are part of.
  waitForLoginToken?: boolean;
  abnHeaders?: {
    userid?: string;
    orgid?: string;
    devicetype?: string;
    usertimezone?: string;
    userlanguage?: string;
  };
  // eslint-disable-next-line sonarjs/cognitive-complexity
}): Promise<{ data: any; status: any; errors: { message: string }[] }> => {
  try {
    // Local tokens only (refreshed if expired); currentAuthenticatedUser() can call Cognito GetUser.
    const idTokenPayload = (await Auth.currentSession()).getIdToken().decodePayload();
    const credentials = await Auth.currentCredentials();

    if (!credentials.accessKeyId || !credentials.secretAccessKey) {
      throw new Error('Invalid AWS credentials');
    }

    const username = idTokenPayload['cognito:username'] || idTokenPayload.email || '';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-PZY-Client-Type': 'Web',
    };

    if (requestId) headers.requestId = requestId;
    // One spelling of userid only: Signer.sign lowercases both userId and userid into two signed
    // entries, but the browser merges them into one header, so the signature no longer matches
    // (403 InvalidSignatureException).
    if (abnHeaders?.userid) headers.userid = abnHeaders.userid;
    else if (userId) headers.userId = userId;
    if (username) headers.username = username;
    if (abnHeaders?.orgid) headers.orgid = abnHeaders.orgid;
    if (abnHeaders?.devicetype) headers.devicetype = abnHeaders.devicetype;
    if (abnHeaders?.usertimezone) headers.usertimezone = abnHeaders.usertimezone;
    if (abnHeaders?.userlanguage) headers.userlanguage = abnHeaders.userlanguage;

    // Absent until the SIGN_IN auth event returns one, so the backend has to work without it.
    Object.assign(headers, waitForLoginToken ? await getLoginTokenHeaderWhenReady() : getLoginTokenHeader());

    const requestOptions: any = { method, url, headers };

    if (body) {
      requestOptions.data = JSON.stringify(body);
    }

    const signedRequest = Signer.sign(
      requestOptions,
      {
        access_key: credentials.accessKeyId,
        secret_key: credentials.secretAccessKey,
        session_token: credentials.sessionToken,
      },
      {
        service: 'execute-api',
        region: awsConfig.aws_project_region,
      }
    );

    const response = await fetch(signedRequest.url, {
      method: signedRequest.method,
      headers: signedRequest.headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const contentLength = response.headers.get('content-length');
    if (response.status === 200 && (contentLength === '0' || contentLength === null)) {
      return { data: null, status: response.status, errors: [] };
    }

    const responseData = await response.json();
    return { data: responseData, status: response.status, errors: [] };
  } catch (error: any) {
    console.error('API call failed:', error);
    return {
      data: null,
      status: null,
      errors: [{ message: error.message || 'Request failed' }],
    };
  }
};

export const extractFacilityIds = (jobs: any) => {
  const facilityIds = jobs?.map((job: { facilityId: any }) => job?.facilityId);
  return [...new Set(facilityIds)];
};

export const convertDefaultValue = (radius: number) => {
  if (radius > 0) {
    return Number((radius / mileInMeters).toFixed(2)); // This is to convert geoFenceRadius from meters to miles
  } else {
    return defaultValueInMiles; // By default we are displaying 800 meters which is 0.49 miles
  }
};

export const isSuccessful = (response: any) => {
  return !isEmpty(response?.data) && isEmpty(response?.errors);
};

const getNameForAddressComponent = (premise: AddressComponent[], type: string): string | null => {
  const foundResult = premise?.length ? premise[0].address_components.filter((item) => item.types.includes(type)) : [];
  return foundResult?.length ? foundResult[0].long_name : null;
};

export const getShortNameForAddressComponent = (premise: AddressComponent[], type: string): string | null => {
  const foundResult = premise?.length ? premise[0].address_components.filter((item) => item.types.includes(type)) : [];
  return foundResult?.length ? foundResult[0].short_name : null;
};

export const getAddressComponents = (addressComponentsResults: AddressComponent[]): CurrentAddress => {
  const address: CurrentAddress = {
    formattedAddress: null,
    googlePlaceId: null,
    country: null,
    state: null,
    stateCode: null,
    city: null,
    postalCode: null,
    latitude: null,
    longitude: null,
    addressLine1: '',
    addressLine2: '',
  };
  const premise = addressComponentsResults.filter(
    (addressItem) => addressItem.types.includes('premise') || addressItem.types.includes('subpremise')
  );

  const selected = premise.length ? premise[0] : null;

  address.formattedAddress = premise.length ? premise[0].formatted_address : null;
  address.googlePlaceId = premise.length ? premise[0].place_id : null;
  address.latitude = selected?.geometry?.location?.lat?.() ?? null;
  address.longitude = selected?.geometry?.location?.lng?.() ?? null;

  address.country = getNameForAddressComponent(premise, 'country') ?? null;
  address.state = getNameForAddressComponent(premise, 'administrative_area_level_1') ?? null;
  address.stateCode = getShortNameForAddressComponent(premise, 'administrative_area_level_1');
  address.city = getNameForAddressComponent(premise, 'locality') ?? null;
  address.postalCode = getNameForAddressComponent(premise, 'postal_code') ?? null;

  const address1 = getNameForAddressComponent(premise, 'street_number') ?? '';
  const address2 = getNameForAddressComponent(premise, 'route') ?? '';
  const address3 = getNameForAddressComponent(premise, 'sublocality_level_2') ?? '';
  const address4 = getNameForAddressComponent(premise, 'sublocality_level_1') ?? '';
  address.addressLine1 = address1 + ' ' + address2;
  address.addressLine2 = address3 + ' ' + address4;

  return address;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getSearchAddressComponents = (searchAddressComponentsResults: AddressComponent[]): CurrentAddress => {
  const address: CurrentAddress = {
    formattedAddress: null,
    googlePlaceId: null,
    country: null,
    state: null,
    stateCode: null,
    city: null,
    postalCode: null,
    latitude: null,
    longitude: null,
    addressLine1: '',
    addressLine2: '',
  };

  const premise = searchAddressComponentsResults.filter(
    (addressItem) =>
      addressItem.types.includes('premise') ||
      addressItem.types.includes('establishment') ||
      addressItem.types.includes('street_address')
  );
  const selected = premise.length ? premise[0] : null;

  address.formattedAddress = premise.length ? premise[0].formatted_address : null;
  address.googlePlaceId = premise.length ? premise[0].place_id : null;
  address.country = getNameForAddressComponent(premise, 'country') ?? null;
  address.state = getNameForAddressComponent(premise, 'administrative_area_level_1') ?? null;
  address.stateCode = getShortNameForAddressComponent(premise, 'administrative_area_level_1');
  address.city = getNameForAddressComponent(premise, 'locality') ?? null;
  address.postalCode = getNameForAddressComponent(premise, 'postal_code') ?? null;

  address.formattedAddress = premise.length ? premise[0].formatted_address : null;
  address.googlePlaceId = premise.length ? premise[0].place_id : null;
  address.latitude = selected?.geometry?.location?.lat?.() ?? null;
  address.longitude = selected?.geometry?.location?.lng?.() ?? null;

  const address1 = getNameForAddressComponent(premise, 'street_number') ?? '';
  const address2 = getNameForAddressComponent(premise, 'route') ?? '';
  const address3 = getNameForAddressComponent(premise, 'sublocality_level_3') ?? '';
  const address4 = getNameForAddressComponent(premise, 'sublocality_level_2') ?? '';
  address.addressLine1 = address1 + '' + address2;
  address.addressLine2 = address3 + '' + address4;

  return address;
};

export const convertToQuarterlyFormat = (dateStr: string): string => {
  const [month, day] = dateStr.split('-').map(Number);
  let quarter = '';
  if (month >= 1 && month <= 3) quarter = 'Q1';
  else if (month >= 4 && month <= 6) quarter = 'Q2';
  else if (month >= 7 && month <= 9) quarter = 'Q3';
  else if (month >= 10 && month <= 12) quarter = 'Q4';
  return `${quarter}-${day}`;
};

// Utility functions to convert between formats
export const FormatDisplayToPayload = (value: string): string => {
  if (!value) return '';
  if (value.startsWith('M')) return value;
  const [month, day] = value.split('-');
  return `M${parseInt(month)}-${parseInt(day)}`;
};

export const FormatPayloadToDisplay = (value: string): string => {
  if (!value) return '';
  if (!value.startsWith('M')) return value;
  const parts = value.slice(1).split('-');
  if (parts.length !== 2) return value;
  const month = parts[0].padStart(2, '0');
  const day = parts[1].padStart(2, '0');
  return `${month}-${day}`;
};

export const GetDayName = (dayNumber: string) => {
  return WEEK_DAYS[parseInt(dayNumber)] || 'Unknown';
};

export const ComposeQuarterEntry = (
  patternType: PatternType,
  config: { month: string; week: string; day: string }
): string => {
  switch (patternType) {
    case PatternTypes.Monthly:
      return `${config.month}-${config.week}-${config.day}`;
    case PatternTypes.Weekly:
      return `${config.week}-${config.day}`;
    case PatternTypes.SpecificDay:
      return `${config.month}-${config.day}`;
    default:
      return '';
  }
};

export const getStatusLabelAndColor = (status: WorkDayStatus) => {
  switch (status) {
    case WorkDayStatus.Scheduled:
      return { label: ScheduleLabels.Scheduled, color: theme.palette.secondary.customColor13 };
    case WorkDayStatus.NoShow:
      return { label: ScheduleLabels.NoShow, color: theme.palette.secondary.customColor12 };
    case WorkDayStatus.Completed:
      return { label: ScheduleLabels.Completed, color: theme.palette.secondary.customColor14 };
    case WorkDayStatus.InProgress:
      return { label: ScheduleLabels.InProgress, color: theme.palette.secondary.customColor15 };
    case WorkDayStatus.InComplete:
      return { label: ScheduleLabels.InComplete, color: theme.palette.secondary.customColor15 };
    case WorkDayStatus.Skipped:
      return { label: ScheduleLabels.Skipped, color: theme.palette.secondary.customColor15 };
    case WorkDayStatus.Rescheduled:
      return { label: ScheduleLabels.Rescheduled, color: theme.palette.secondary.customColor15 };
    default:
      return { label: '', color: '' };
  }
};

export const isToday = (dateStr: string | Date): boolean => {
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  const today = new Date();

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

export const isValidDate = (date: Date | string | null | undefined): boolean => {
  if (!date) return false;
  const parsedDate = typeof date === 'string' ? new Date(date) : date;
  return parsedDate instanceof Date && !isNaN(parsedDate.getTime());
};

export const monthMap: Record<string, number> = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12,
};

export const quarterMap: Record<string, string> = {
  'Quarter 1': 'Q1',
  'Quarter 2': 'Q2',
  'Quarter 3': 'Q3',
  'Quarter 4': 'Q4',
};

export const monthToQuarterIndex: Record<string, number> = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 2,
  May: 3,
  Jun: 4,
  Jul: 2,
  Aug: 3,
  Sep: 3,
  Oct: 4,
  Nov: 1,
  Dec: 2,
};

export const monthIndex: Record<string, number> = { ...monthMap }; // same as monthMap, just named differently for clarity

export function getMonthlyQuarterlyScheduleDays(
  quarters: Record<string, { month?: string; week?: string; day?: string }>,
  applyToAllQuarters: boolean
) {
  if (applyToAllQuarters && quarters['All Quarters']) {
    const { month, week, day } = quarters['All Quarters'];
    const m = `M${monthMap[month?.split(' - ')[0] ?? '']}`;
    return `Q-${m}-${week}-${day}`;
  }

  return Object.entries(quarters as Record<string, { month: string; week: string; day: string }>)
    .filter(([q]) => q.startsWith('Quarter'))
    .map(([quarter, { month, week, day }]: [string, { month: string; week: string; day: string }]) => {
      const qKey = quarterMap[quarter];
      const m = `M${monthMap[month]}`;
      return `${qKey}-${m}-${week}-${day}`;
    })
    .join(',');
}

export function getWeeklyQuarterlyScheduleDays(
  quarters: Record<string, { week?: string; day?: string }>,
  applyToAllQuarters: boolean
) {
  if (applyToAllQuarters && quarters['All Quarters']?.week && quarters['All Quarters']?.day) {
    const { week, day } = quarters['All Quarters'];
    return `Q-${week}-${day}`;
  }

  return Object.entries(quarters)
    .filter(([quarter]) => quarterMap[quarter])
    .map(([quarter, { week, day }]) => {
      const qKey = quarterMap[quarter];

      const weekNum = parseInt((week || '0').replace('W', ''), 10);
      const newWeek = weekNum - 6;

      const dayNum = parseInt(day || '0', 10);

      return `${qKey}-W${newWeek}-${dayNum}`;
    })
    .join(',');
}

export function getSpecificDayQuarterlyScheduleDays(
  quarters: Record<string, { month?: string; day?: string }>,
  applyToAllQuarters: boolean
) {
  if (applyToAllQuarters && quarters['All Quarters']) {
    const { month, day } = quarters['All Quarters'];
    const firstMonth = month?.split(' - ')[0] || '';
    const mIndex = monthMap[firstMonth] || 1;
    const d = day ? day.padStart(2, '0') : '01';
    return `Q-M${mIndex}-${d}`;
  }

  return Object.entries(quarters)
    .filter(([quarter]) => quarterMap[quarter])
    .map(([quarter, { month, day }]) => {
      const qKey = quarterMap[quarter];

      const mIndex = monthToQuarterIndex[month || ''] || 1;
      const d = day ? day?.padStart(2, '0') : '01';

      return `${qKey}-M${mIndex}-${d}`;
    })
    .join(',');
}

export function convertToMinutes(hours: number, minutes: number): number {
  if (!hours && !minutes) return 0;
  return (hours || 0) * 60 + (minutes || 0);
}

export const getStateKey = (quarter: string, type: string) => `${quarter}-${type}`;

export const getGraceMinutes = (hours?: number, minutes?: number): number | undefined => {
  return convertToMinutes(hours ?? 0, minutes ?? 0);
};

export const convertMinutesToHours = (minutes: number) => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return { hrs, mins };
};

export const toMinutes = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

export const hasAccessToConversations = (jobStatus?: string, jobCustodian?: string): boolean => {
  const authValue = localStorage.getItem('auth');
  let userType: UserType = UserType.NoUser;
  if (authValue !== null) {
    const authData = JSON.parse(authValue);
    userType = authData?.attributes?.['custom:Role'] as UserType;
  }
  const { customerId = '' } = getStoredCustomerDetails() || {};

  if (
    (jobStatus === JobStatuses.PendingApproval || (jobStatus === JobStatuses.OpenToBid && jobCustodian)) &&
    userType === UserType.Customer
  ) {
    return false;
  }

  if (userType === UserType.Customer || (userType === UserType.Vendor && customerId !== velocitiOrganizationId)) {
    return true;
  } else {
    const processData = getProcessData();

    return processData?.some(
      (process: { processId: string | number }) => String(process?.processId) === USER_PROCESSES.Conversations
    );
  }
};

export const hasEventId = (processEvents: IProcessEvents[], id: string) => {
  return processEvents?.some((event: { eventId: string | number }) => String(event?.eventId) === id);
};

export const hasEventForReport = (processEvents: IProcessEvents[], reportType: string) => {
  if (!reportType || typeof reportType !== 'string' || !(reportType in REPORT_TYPE_TO_EVENT_KEY)) return false;

  const eventKey = REPORT_TYPE_TO_EVENT_KEY[reportType as keyof typeof REPORT_TYPE_TO_EVENT_KEY];
  if (!eventKey) return false;

  const eventId = String(PROCESS_EVENTS[eventKey as keyof typeof PROCESS_EVENTS]);
  return processEvents?.some((event: { eventId: string | number }) => String(event?.eventId) === eventId);
};

export const hasAccessToTab = (processEvents: IProcessEvents[], route: string) => {
  const eventKey = MARKETPLACE_ROUTE_TO_EVENT_KEY[route];
  if (!eventKey) return false;

  const eventId = String(PROCESS_EVENTS[eventKey]);
  return processEvents?.some((event: { eventId: string | number }) => String(event?.eventId) === eventId);
};

export const hasAccessToJobsTab = (processEvents: IProcessEvents[], route: string) => {
  const eventKey = JOBS_ROUTE_TO_EVENT_KEY[route];
  if (!eventKey) return false;

  const eventId = PROCESS_EVENTS[eventKey];
  return processEvents?.some((event: { eventId: string | number }) => String(event?.eventId) === eventId);
};

export const getShowActionsColumn = (
  type?: string,
  canShowActionsForJobs?: boolean,
  canShowActionsForSchedules?: boolean
) => {
  return type === JobManagementType.AllJobs
    ? !!canShowActionsForJobs
    : type === JobManagementType.AllSchedules
      ? !!canShowActionsForSchedules
      : true;
};

export enum VendorType {
  SELF = 'SELF',
  SUBCONTRACT = 'SUBCONTRACT',
  NONE = 'NONE',
}

export enum VendorAssignmentAction {
  ADD = 'ADD',
  UPDATE = 'UPDATE',
  REMOVE = 'REMOVE',
}

export const sortByLegalName = (customers: ILoginCustomer[]): ILoginCustomer[] =>
  [...customers].sort((a, b) => (a.legalName ?? '').localeCompare(b.legalName ?? ''));
